import React from 'react';
import { Product } from '../../types/product';
import { useInventory } from '../../hooks/useInventory';
import InventoryList from '../warehouse/InventoryList';
import InventoryMovementsList from '../warehouse/InventoryMovementsList';
import {
  CubeIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  TagIcon,
  XMarkIcon,
  CheckIcon,
  ShoppingBagIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  PhotoIcon,
  ArchiveBoxIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { useLanguage } from 'contexts/LanguageContext';
import ImageModal from '../common/ImageModal';
import { Tab } from '@headlessui/react';

// Cache-buster függvény a képekhez
const addCacheBuster = (url: string | undefined): string => {
  if (!url) return '';
  const timestamp = Date.now();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${timestamp}`;
};

interface ProductViewProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProductView: React.FC<ProductViewProps> = ({ product, isOpen, onClose }) => {
  const { t } = useLanguage();
  const { inventory, fetchInventory } = useInventory();

  // Készlet betöltése termék megnyitásakor
  React.useEffect(() => {
    if (product && isOpen) {
      fetchInventory({ productId: product.id });
    }
  }, [product, isOpen, fetchInventory]);

  // Termék készletének összegzése
  const productInventorySummary = React.useMemo(() => {
    if (!product) return null;
    const productInventory = inventory.filter(item => item.productId === product.id);
    
    let totalQuantity = 0;
    let availableQuantity = 0;
    const warehouses: Array<{name: string, quantity: number, available: number}> = [];
    
    productInventory.forEach(item => {
      const available = Math.max(0, (item.quantity || 0) - (item.reservedQuantity || 0));
      totalQuantity += item.quantity || 0;
      availableQuantity += available;
      warehouses.push({
        name: item.warehouseName || 'Ismeretlen raktár',
        quantity: item.quantity || 0,
        available: available
      });
    });
    
    return {
      totalQuantity,
      availableQuantity,
      warehouses
    };
  }, [inventory, product]);

  if (!product) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${product.name} (${product.code})`}
      size="6xl"
    >
      <div className="p-4 space-y-5">
        {/* Fejléc - Termék alapinformációk */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 border-b border-gray-200 dark:border-gray-700 pb-4">
          {/* Termék kép, ha van */}
          {(product.thumbnailUrl || (product.images && product.images.length > 0)) && (
            <div className="flex-shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
              <ImageModal 
                src={addCacheBuster(product.thumbnailUrl || (product.images && product.images[0]?.url) || '')} 
                alt={product.name}
                thumbnailClassName="w-full h-full object-contain p-1"
                highQualitySrc={addCacheBuster(product.images && product.images[0]?.url)}
              />
            </div>
          )}
          
          <div className="flex flex-1 items-center gap-4">
            <div className={`p-3 rounded-lg ${product.type === 'product' 
              ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' 
              : 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'}`}
            >
              {product.type === 'product' 
                ? <CubeIcon className="w-6 h-6" /> 
                : <WrenchScrewdriverIcon className="w-6 h-6" />
              }
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{product.name}</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">#{product.code}</span>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  product.isActive 
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400' 
                    : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                }`}>
                  {product.isActive ? t('common.active') : t('common.inactive')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Képgaléria, ha több kép van */}
        {product.images && product.images.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
              {t('products.sections.images') || 'Termékképek'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {product.images.map((image, index) => (
                <div 
                  key={index} 
                  className={`aspect-square rounded-md border overflow-hidden bg-white dark:bg-gray-700 ${
                    image.isMain ? 'border-primary-500 dark:border-primary-400 ring-2 ring-primary-200 dark:ring-primary-700' : 'border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <ImageModal 
                    src={addCacheBuster(image.url)} 
                    alt={`${product.name} - ${index + 1}`}
                    thumbnailClassName="w-full h-full object-contain p-1"
                    highQualitySrc={addCacheBuster(image.url)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Kategória és részletek */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Bal oszlop */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
              {t('products.sections.details')}
            </h3>
            
            <dl className="grid grid-cols-2 gap-4">
              {product.category && (
                <div className="col-span-2">
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.category')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200 flex items-center">
                    {product.category.fullPath || product.category.level1 || '-'}
                  </dd>
                </div>
              )}
              
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.type')}</dt>
                <dd className="font-medium text-gray-800 dark:text-gray-200 flex items-center">
                  {product.type === 'product' ? (
                    <>
                      <CubeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-1" />
                      {t('products.types.product')}
                    </>
                  ) : (
                    <>
                      <WrenchScrewdriverIcon className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-1" />
                      {t('products.types.service')}
                    </>
                  )}
                </dd>
              </div>
              
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.unit')}</dt>
                <dd className="font-medium text-gray-800 dark:text-gray-200">{product.unit}</dd>
              </div>
              
              {product.barcode && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.barcode')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">{product.barcode}</dd>
                </div>
              )}
              
              {product.articleNumber && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.articleNumber')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">{product.articleNumber}</dd>
                </div>
              )}
              
              {product.vtsz && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.vtsz')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">{product.vtsz}</dd>
                </div>
              )}
              
              {product.currentStock !== undefined && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.stock')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">
                    {product.currentStock} {product.unit}
                  </dd>
                </div>
              )}
            </dl>
          </div>
          
          {/* Jobb oszlop - Árazás */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
              {t('products.sections.pricing')}
            </h3>
            
            <dl className="grid grid-cols-2 gap-4">
              {product.netPrice !== undefined && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.netPrice')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">
                    {product.netPrice.toLocaleString()} Ft
                  </dd>
                </div>
              )}
              
              {product.taxRate && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.taxRate')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">{product.taxRate}</dd>
                </div>
              )}
              
              {product.netPrice !== undefined && product.taxRate && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.grossPrice')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">
                    {(product.taxRate === '27%' 
                      ? Math.round(product.netPrice * 1.27) 
                      : product.taxRate === '18%' 
                        ? Math.round(product.netPrice * 1.18)
                        : product.taxRate === '5%' 
                          ? Math.round(product.netPrice * 1.05)
                          : product.netPrice
                    ).toLocaleString()} Ft
                  </dd>
                </div>
              )}
              
              {product.accountingPrice !== undefined && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.accountingPrice')}</dt>
                  <dd className="font-medium text-gray-800 dark:text-gray-200">
                    {product.accountingPrice.toLocaleString()} Ft
                  </dd>
                </div>
              )}
            </dl>
            
            {/* Készletinformációk */}
            {productInventorySummary && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3 flex items-center">
                  <ArchiveBoxIcon className="w-3 h-3 mr-1" />
                  {t('products.sections.inventory')}
                </h4>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('inventory.available')}</dt>
                    <dd className={`font-medium ${productInventorySummary.availableQuantity > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {productInventorySummary.availableQuantity} {product.unit}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('inventory.quantity')}</dt>
                    <dd className="font-medium text-gray-800 dark:text-gray-200">
                      {productInventorySummary.totalQuantity} {product.unit}
                    </dd>
                  </div>
                </dl>
                {/* Raktári bontás */}
                <div className="mt-3">
                  <div className="flex flex-wrap gap-1">
                    {productInventorySummary.warehouses
                      .filter(wh => wh.quantity > 0)
                      .map((wh, idx) => (
                      <span 
                        key={idx} 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          wh.available > 0 
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {wh.name}: {wh.available}
                        {wh.quantity !== wh.available && (
                          <span className="text-gray-400 dark:text-gray-500">/{wh.quantity}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* További jellemzők */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
            {t('products.sections.additionalPricing')}
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col items-center">
              <ShoppingBagIcon className={`w-4 h-4 mb-1 ${product.webStore ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} />
              <span className="text-xs text-center text-gray-700 dark:text-gray-300">{t('products.fields.webStore')}</span>
              <div className="mt-1">
                {product.webStore ? (
                  <CheckIcon className="w-3 h-3 text-green-600 dark:text-green-400" />
                ) : (
                  <XMarkIcon className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col items-center">
              <BanknotesIcon className={`w-4 h-4 mb-1 ${product.reverseTaxation ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
              <span className="text-xs text-center text-gray-700 dark:text-gray-300">{t('products.fields.reverseTaxation')}</span>
              <div className="mt-1">
                {product.reverseTaxation ? (
                  <CheckIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                ) : (
                  <XMarkIcon className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col items-center">
              <ReceiptPercentIcon className={`w-4 h-4 mb-1 ${product.discontinued ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`} />
              <span className="text-xs text-center text-gray-700 dark:text-gray-300">{t('products.fields.discontinued')}</span>
              <div className="mt-1">
                {product.discontinued ? (
                  <CheckIcon className="w-3 h-3 text-red-600 dark:text-red-400" />
                ) : (
                  <XMarkIcon className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </div>
            
            {product.purchasePrice !== undefined && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('products.fields.purchasePrice')}</div>
                <div className="font-medium text-gray-800 dark:text-gray-200">{product.purchasePrice.toLocaleString()} Ft</div>
              </div>
            )}
          </div>
        </div>

        {/* Leírás (ha van) */}
        {product.description && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
              {t('products.fields.description')}
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
              {product.description}
            </div>
          </div>
        )}

        {/* Készlet és mozgások tab-os nézete */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <Tab.Group>
            <Tab.List className="flex border-b border-gray-200 dark:border-gray-600">
              <Tab
                className={({ selected }) =>
                  `py-3 px-6 text-sm font-medium outline-none ${
                    selected
                      ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 dark:border-primary-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`
                }
              >
                <div className="flex items-center">
                  <ArchiveBoxIcon className="w-3 h-3 mr-2" />
                  {t('products.sections.inventoryDetails')}
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  `py-3 px-6 text-sm font-medium outline-none ${
                    selected
                      ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500 dark:border-primary-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`
                }
              >
                <div className="flex items-center">
                  <ClockIcon className="w-3 h-3 mr-2" />
                  {t('products.sections.stockMovements')}
                </div>
              </Tab>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel className="p-4">
                <InventoryList productId={product.id} />
              </Tab.Panel>
              <Tab.Panel className="p-4">
                <InventoryMovementsList productId={product.id} limit={50} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>

        <div className="flex justify-end mt-4">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProductView; 